// BUILD-042 — Company directory (Owner-approved visual layout, 2026-10-06):
// docs/superpowers/decisions/2026-10-06-build-042-approved-company-directory-layout.md
// Reference: docs/superpowers/references/2026-10-06-build-042-company-directory.html
//
// "A work-contact lookup, not an employee HR file or account-management screen... name, optional
// profile photo, job title, department, designated work phone and designated work email only."
//
// Oct 20 (persistent pilot): this screen now shows REAL people from GET /hr/directory, a strict allow-list projection
// { employeeId, name, workEmail, title, department } served to any signed-in, active employee. There is no hardcoded or fallback roster:
// while loading it says so, on failure it says so (with a retry), and an empty company shows an empty state. Real, fictional and mixed
// people appear exactly as stored. Personal contact information, classification, schedule, manager, dates, compensation and the
// HR-record flag are never part of the response and so can never be shown here.
//
// The designated WORK PHONE is not part of this release's directory source (the read-only lookup behind it carries name and work
// email only), so the phone row says "Not provided" and Call is disabled rather than showing an invented number. Email opens the
// person's own mail application through a plain mailto: link.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCompanyDirectory, type CompanyDirectoryPerson } from '../../api/client';
import { Breadcrumbs } from './workspace/Breadcrumbs';
import { Avatar } from './workspace/Avatar';

interface DirectoryPerson {
  id: string;
  name: string;
  title: string;
  department: string;
  /** "Not provided" when empty - the work phone is not in this release's directory source. */
  workPhone: string;
  workEmail: string;
}

function toPerson(p: CompanyDirectoryPerson): DirectoryPerson {
  return {
    id: p.employeeId,
    name: p.name,
    title: p.title ?? '',
    department: p.department ?? '',
    workPhone: '',
    workEmail: p.workEmail ?? '',
  };
}

export function DirectoryTab() {
  const [people, setPeople] = useState<DirectoryPerson[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoadFailed(false);
    setPeople(null);
    getCompanyDirectory()
      .then((d) => setPeople((d.people ?? []).map(toPerson)))
      .catch(() => { setPeople([]); setLoadFailed(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const all = people ?? [];

  // Derived from the actual roster, so every listed option returns at least one person (never a dead filter choice).
  const departmentOptions = useMemo(
    () => Array.from(new Set(all.map((p) => p.department).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [all]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all.filter((p) =>
      (!term || `${p.name} ${p.title}`.toLowerCase().includes(term)) &&
      (!department || p.department === department)
    );
  }, [all, search, department]);

  useEffect(() => {
    // Remove a stale selection excluded by filtering, per the decision's acceptance criteria -
    // never leave the detail panel showing someone no longer in the visible result set.
    if (selectedId && !filtered.some((p) => p.id === selectedId)) {
      setSelectedId(null);
    }
  }, [filtered, selectedId]);

  const selected = selectedId ? all.find((p) => p.id === selectedId) ?? null : null;
  const hasFilters = search.trim().length > 0 || department.length > 0;

  function clearFilters() {
    setSearch('');
    setDepartment('');
  }

  return (
    <div>
      <Breadcrumbs items={[{ label: 'HR', to: '/hr' }, { label: 'Company directory' }]} />

      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <div>
          <h1 className="text-xl font-bold text-gray-900 leading-tight">Company directory</h1>
          <p className="text-sm text-gray-500 mt-1">Find a colleague and their work contact information.</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mt-4 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_190px] gap-4">
        <div>
          <label htmlFor="directory-search" className="block text-xs font-medium text-gray-700 mb-1.5">Name or job title</label>
          <div className="relative">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
            </svg>
            <input
              id="directory-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search people or job titles"
              autoComplete="off"
              className="w-full border border-gray-300 rounded-md pl-9 pr-3 py-2 text-sm"
            />
          </div>
        </div>
        <div>
          <label htmlFor="directory-department" className="block text-xs font-medium text-gray-700 mb-1.5">Department</label>
          <select
            id="directory-department"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="">All departments</option>
            {departmentOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 py-3 text-xs text-gray-500">
        <span aria-live="polite">
          {people === null ? 'Loading people…' : filtered.length === all.length ? `${all.length} people` : `${filtered.length} of ${all.length} people`}
        </span>
        {hasFilters && (
          <button type="button" onClick={clearFilters} className="text-[#4AABDE] hover:underline">
            Clear filters
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_248px] gap-4 items-start">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" aria-label="Employee results">
          {people === null ? (
            <div role="status" className="sm:col-span-2 bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-sm text-gray-500">
              Loading the company directory…
            </div>
          ) : loadFailed ? (
            <div role="alert" data-testid="directory-load-failed" className="sm:col-span-2 bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-sm text-gray-700">
              The company directory could not be loaded. Nothing is shown rather than guessing.{' '}
              <button type="button" onClick={load} className="text-[#4AABDE] hover:underline">Try again</button>
            </div>
          ) : all.length === 0 ? (
            <div data-testid="directory-empty" className="sm:col-span-2 bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-sm text-gray-500">
              No colleagues are listed yet.
            </div>
          ) : filtered.length === 0 ? (
            <div className="sm:col-span-2 bg-white rounded-lg border border-gray-200 shadow-sm p-6 text-sm text-gray-500">
              No colleagues match these filters. Try another name, job title or department.
            </div>
          ) : (
            filtered.map((p) => {
              const isSelected = selectedId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedId(p.id)}
                  aria-pressed={isSelected}
                  className={`relative text-left border rounded-lg p-4 flex flex-col items-start gap-1 min-w-0 ${
                    isSelected ? 'border-[#4AABDE] bg-[#EDF5FC] shadow-[inset_0_0_0_1px_#4AABDE]' : 'border-gray-200 bg-white hover:bg-[#EDF5FC]'
                  }`}
                >
                  <Avatar name={p.name} colorKey={p.id} sizeClass="w-[46px] h-[46px]" textClass="text-sm" />
                  <strong className="text-sm font-semibold text-gray-900 break-words mt-1">{p.name}</strong>
                  <span className="text-xs text-gray-500 break-words">{p.title || 'No job title recorded'}</span>
                  {p.department && <span className="text-[11px] text-gray-400 mt-0.5">{p.department}</span>}
                  <svg className={`absolute right-3 top-4 w-4 h-4 ${isSelected ? 'text-[#4AABDE]' : 'text-gray-300'}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    {isSelected
                      ? <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      : <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />}
                  </svg>
                </button>
              );
            })
          )}
        </div>

        <section aria-label="Selected work contact" aria-live="polite" className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden min-w-0">
          {selected ? (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 text-xs font-medium text-gray-500">
                <span>Work contact</span>
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  aria-label={`Close work contact for ${selected.name}`}
                  className="p-0.5 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="px-4 pt-5 pb-4 text-center">
                <div className="mx-auto mb-3" style={{ width: 'fit-content' }}>
                  <Avatar name={selected.name} colorKey={selected.id} sizeClass="w-[72px] h-[72px]" textClass="text-xl" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 break-words">{selected.name}</h2>
                <p className="text-xs text-gray-500 mt-1 break-words">{selected.title || 'No job title recorded'}</p>
                {selected.department && <span className="inline-block mt-2.5 bg-[#eef1f6] rounded px-2.5 py-1 text-xs text-gray-600">{selected.department}</span>}
              </div>
              <div className="px-4 pb-4">
                <dl>
                  <div className="py-3 border-t border-gray-100">
                    <dt className="text-[11px] text-gray-400 mb-1">Work phone</dt>
                    <dd className="text-sm font-medium text-gray-900 break-words">{selected.workPhone || 'Not provided'}</dd>
                  </div>
                  <div className="py-3 border-t border-gray-100">
                    <dt className="text-[11px] text-gray-400 mb-1">Work email</dt>
                    <dd className="text-sm font-medium text-gray-900 break-words">{selected.workEmail || 'Not provided'}</dd>
                  </div>
                </dl>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button
                    type="button"
                    disabled
                    title="No work phone is listed for this person"
                    className="flex items-center justify-center gap-1.5 border border-gray-200 rounded-md py-2 text-xs text-[#4AABDE] disabled:opacity-50 disabled:cursor-default"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h1.5a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                    </svg>
                    Call
                  </button>
                  <a
                    href={selected.workEmail ? `mailto:${selected.workEmail}` : undefined}
                    aria-disabled={!selected.workEmail}
                    className={`flex items-center justify-center gap-1.5 border border-transparent rounded-md py-2 text-xs text-white bg-[#0876b0] ${selected.workEmail ? '' : 'opacity-50 pointer-events-none'}`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                    </svg>
                    Email
                  </a>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-gray-100 text-xs font-medium text-gray-500">Work contact</div>
              <div className="text-center text-sm text-gray-500 px-4 py-10">
                Select a colleague to see their work phone and email.
              </div>
            </>
          )}
        </section>
      </div>

      <p className="text-[11px] text-gray-400 mt-4">Work contact information only.</p>

      <details className="text-xs text-gray-500 mt-4">
        <summary className="text-[#4AABDE] cursor-pointer">What this directory shows</summary>
        <div className="mt-2 max-w-lg space-y-1.5">
          <p>Name, job title, department and work email, taken from each person's current profile and HR employment record. A person without an HR record still appears, without a job title or department.</p>
          <p>Personal contact information (home address, personal phone/email, emergency contacts) is private to each Employee and HR Administration and is never shown here.</p>
        </div>
      </details>
    </div>
  );
}
